import { useApprove } from 'src/hooks/v3/useApprove';
import {
  IDerivedMintInfoWithProvider,
  useMintActionHandlers
} from 'src/state/mintStore';
import { ApprovalState } from 'src/types/approve-state';
import { Currency, Field } from '@cryptoalgebra/kim-sdk';
import React from 'react';
import { Address } from '@thirdweb-dev/sdk';
import {
  useAddress,
  useChain,
  useContract,
  useSwitchChain
} from '@thirdweb-dev/react';
import { useAsyncFn } from 'react-use';
import { HStack } from '@chakra-ui/react';
import { getActiveChain } from '@app/constants/chains';
import { CustomConnectWalletButton } from '@app/components/CustomConnectWalletButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { NEW_WEETH_TOKEN, OLD_WEETH_TOKEN } from '@app/constants/tokens';
import { BigNumber, utils } from 'ethers';
import SteerProxyHelperAbi from '../../../../../../abis/steer/SteerPeripheryHelper.json';
import { useNftTokens } from '@app/hooks/positions/useNftTokens';
import { useConfig } from '@app/config';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface AddLiquidityButtonProps {
  baseCurrency: Currency | undefined | null;
  quoteCurrency: Currency | undefined | null;
  mintInfo: IDerivedMintInfoWithProvider;
  poolAddress: Address | undefined;
  onComplete?: (txHash?: string) => void;
}

export const AddLiquidityWithProviderButton = ({
  baseCurrency,
  quoteCurrency,
  mintInfo,
  onComplete
}: AddLiquidityButtonProps) => {
  const account = useAddress();
  const queryClient = useQueryClient();
  const chain = useChain();
  const switchChain = useSwitchChain();
  const config = useConfig();
  const isNetworkMismatch = useNetworkMismatch();

  const { onFieldBInput, onFieldAInput } = useMintActionHandlers(false);
  const { updateNftTokens, tokens } = useNftTokens(account);

  const { approvalState: approvalStateA, approvalCallback: approvalCallbackA } =
    useApprove(
      mintInfo.parsedAmounts[Field.CURRENCY_A],
      config?.STEER.PROXY_HELPER
    );
  const { approvalState: approvalStateB, approvalCallback: approvalCallbackB } =
    useApprove(
      mintInfo.parsedAmounts[Field.CURRENCY_B],
      config?.STEER.PROXY_HELPER
    );

  const isReady =
    approvalStateA === ApprovalState.APPROVED &&
    approvalStateB === ApprovalState.APPROVED;

  const { contract } = useContract(
    config?.STEER.PROXY_HELPER,
    SteerProxyHelperAbi
  );

  const [{ loading: isAddingLiquidityLoading }, addLiquidity] =
    useAsyncFn(async () => {
      if (!contract) {
        return;
      }

      try {
        const _amount0 = utils.parseUnits(
          mintInfo?.parsedAmounts[Field.CURRENCY_A]?.toExact() ?? '',
          mintInfo?.parsedAmounts[Field.CURRENCY_A]?.currency.decimals
        );

        const _amount1 = utils.parseUnits(
          mintInfo?.parsedAmounts[Field.CURRENCY_B]?.toExact() ?? '',
          mintInfo?.parsedAmounts[Field.CURRENCY_B]?.currency.decimals
        );

        const [token0Address, token1Address] = mintInfo.isSwappedPair
          ? [
              mintInfo?.parsedAmounts[Field.CURRENCY_B]?.currency.wrapped
                .address,
              mintInfo?.parsedAmounts[Field.CURRENCY_A]?.currency.wrapped
                .address
            ]
          : [
              mintInfo?.parsedAmounts[Field.CURRENCY_A]?.currency.wrapped
                .address,
              mintInfo?.parsedAmounts[Field.CURRENCY_B]?.currency.wrapped
                .address
            ];

        const [amount0, amount1] = mintInfo.isSwappedPair
          ? [_amount1, _amount0]
          : [_amount0, _amount1];

        const res = await contract.call('deposit', [
          mintInfo.vaultAddress,
          token0Address,
          token1Address,
          amount0,
          amount1,
          BigNumber.from(0),
          BigNumber.from(0),
          mintInfo.nftPoolAddress,
          +mintInfo.lockDuration * 3600 * 24 // lock duration
        ]);

        if (res?.reason) {
          showErrorToast('Transaction rejected');
        }

        const txHash = res?.receipt?.transactionHash;

        if (txHash) {
          onFieldAInput('');
          onFieldBInput('');

          await queryClient.invalidateQueries([
            QueryKeys.ELIGIBLE_TO_REFERRAL,
            account
          ]);

          await updateNftTokens([...(tokens ?? []), mintInfo.vaultAddress!]);

          showSuccessToast(
            `Transaction successful.`,
            <ExternalLink txHash={txHash} />
          );

          if (onComplete) {
            onComplete(txHash);
          }
        }
      } catch (e: any) {
        if (e?.reason === 'user rejected transaction') {
          return;
        }

        showErrorToast(`Transaction failed`);

        console.error(e.message);
      }
    }, [contract, account, queryClient, mintInfo]);

  const isUnsupportedTokenSelected =
    (baseCurrency?.wrapped.address.toLowerCase() === OLD_WEETH_TOKEN &&
      quoteCurrency?.wrapped.address.toLowerCase() !== NEW_WEETH_TOKEN) ||
    (quoteCurrency?.wrapped.address.toLowerCase() === OLD_WEETH_TOKEN &&
      baseCurrency?.wrapped.address.toLowerCase() !== NEW_WEETH_TOKEN);

  if (isUnsupportedTokenSelected) {
    return (
      <SubmitButton
        label="Unsupported token selected"
        isLoading={false}
        isDisabled
        onClick={() => {}}
      />
    );
  }

  if (!account) return <CustomConnectWalletButton />;

  if (isNetworkMismatch)
    return (
      <SubmitButton
        label="Switch chain"
        isLoading={false}
        variant={'destructive'}
        onClick={() => switchChain(getActiveChain().chainId)}
      />
    );

  if (
    approvalStateA === ApprovalState.NOT_APPROVED ||
    approvalStateA === ApprovalState.PENDING ||
    approvalStateB === ApprovalState.NOT_APPROVED ||
    approvalStateB === ApprovalState.PENDING
  )
    return (
      <HStack w="100%" justifyContent="space-between">
        {(approvalStateA === ApprovalState.NOT_APPROVED ||
          approvalStateA === ApprovalState.PENDING) && (
          <SubmitButton
            label={
              approvalStateA === ApprovalState.PENDING
                ? 'Loading...'
                : `Approve ${mintInfo.parsedAmounts[Field.CURRENCY_A]?.currency
                    .symbol}`
            }
            isLoading={approvalStateA === ApprovalState.PENDING}
            disabled={approvalStateA === ApprovalState.PENDING}
            onClick={() => approvalCallbackA && approvalCallbackA()}
          />
        )}
        {(approvalStateB === ApprovalState.NOT_APPROVED ||
          approvalStateB === ApprovalState.PENDING) && (
          <SubmitButton
            isLoading={approvalStateB === ApprovalState.PENDING}
            onClick={() => approvalCallbackB && approvalCallbackB()}
            label={
              approvalStateB === ApprovalState.PENDING
                ? 'Loading...'
                : `Approve ${mintInfo.parsedAmounts[Field.CURRENCY_B]?.currency
                    .symbol}`
            }
          />
        )}
      </HStack>
    );
  //
  // if (mintInfo.errorMessage)
  //   return (
  //     <SubmitButton label={mintInfo.errorMessage} disabled isLoading={false} />
  //   );

  return (
    <SubmitButton
      isLoading={isAddingLiquidityLoading}
      disabled={!isReady}
      onClick={() => addLiquidity && addLiquidity()}
      label={isAddingLiquidityLoading ? 'Loading...' : 'Create Position'}
    />
  );
};

export default AddLiquidityWithProviderButton;
